import { useTranslation } from "react-i18next";

import { compareMoney, fractionOfMoney } from "@/shared/lib";
import type { Money } from "@/shared/lib";
import { Button } from "@/shared/ui";

/**
 * Быстрый выбор суммы: MIN · 25% · 50% · 75% · MAX (ТЗ §16, §18).
 *
 * Кнопки, а не `Segmented`. `Segmented` — контролируемый переключатель с
 * `aria-pressed`, то есть подсвеченный вариант обязан оставаться истиной; здесь
 * же сразу после нажатия пользователь правит сумму руками, и подсветка начала
 * бы врать. Пресет — одноразовое действие, а не состояние.
 *
 * Доля считается `fractionOfMoney` в `shared/lib/money.ts` — единственном
 * месте, где правило ТЗ №5 разрешает арифметику над деньгами.
 *
 * Пресет ниже минимума ГАСНЕТ, а не поднимается до минимума молча: подпись
 * «25%» обязана означать 25%. Минимум и остаток всё равно перепроверяет бэкенд.
 */
const FRACTIONS = [25, 50, 75] as const;

interface AmountPresetsProps {
  /** Из чего берём долю — баланс или доступный остаток. */
  balance: Money;
  /** Минимальная сумма операции: ниже неё пресет неактивен. */
  min: Money;
  onPick: (amount: Money) => void;
}

export function AmountPresets({ balance, min, onPick }: AmountPresetsProps) {
  const { t } = useTranslation();

  const options: { key: string; label: string; amount: Money }[] = [
    { key: "min", label: t("amountPresets.min"), amount: min },
    ...FRACTIONS.map((percent) => ({
      key: String(percent),
      label: t("amountPresets.fraction", { percent }),
      amount: fractionOfMoney(balance, percent),
    })),
    { key: "max", label: t("amountPresets.max"), amount: balance },
  ];

  return (
    <div className="flex gap-1.5" role="group" aria-label={t("amountPresets.label")}>
      {options.map((option) => (
        <Button
          key={option.key}
          size="pill"
          variant="ghost"
          haptic="select"
          className="flex-1 px-0"
          // Недостижимая сумма не должна выглядеть доступной: и ниже минимума
          // робота, и выше баланса — это отказ бэкенда, полученный заранее.
          disabled={
            compareMoney(option.amount, min) < 0 || compareMoney(option.amount, balance) > 0
          }
          onClick={() => {
            onPick(option.amount);
          }}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
